import { useEffect, useRef, useState } from 'react';
import { inputClass } from '@/components/base/Field';
import type { LessonTemplate, LessonTemplateField } from '@/types';

interface TemplateTextareaProps {
  label: string;
  field: LessonTemplateField;
  value: string;
  onChange: (value: string) => void;
  templates: LessonTemplate[];
  onSaveTemplate: (field: LessonTemplateField, content: string) => Promise<void>;
  onDeleteTemplate: (id: string) => Promise<void>;
  placeholder?: string;
  rows?: number;
}

export default function TemplateTextarea({
  label,
  field,
  value,
  onChange,
  templates,
  onSaveTemplate,
  onDeleteTemplate,
  placeholder,
  rows = 3,
}: TemplateTextareaProps) {
  const [open, setOpen] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  // 기존 내용이 있으면 줄바꿈으로 이어 붙여요.
  const insert = (content: string) => {
    const snippet = content.trim();
    if (!snippet) return;
    const next = value.trim() ? `${value.trimEnd()}\n${snippet}` : snippet;
    onChange(next);
  };

  const handleSave = async () => {
    const current = value.trim();
    if (!current) return;
    if (templates.some((t) => t.content.trim() === current)) {
      setSavedFlash(true);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setSavedFlash(false), 1500);
      return;
    }
    try {
      await onSaveTemplate(field, current);
      setSavedFlash(true);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } catch {
      // 저장 실패는 조용히 무시하고 화면 흐름을 막지 않아요.
    }
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground-800">{label}</span>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap ${
            open
              ? 'border-secondary-400 bg-secondary-100 text-secondary-900'
              : 'border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
          }`}
        >
          <i className="ri-bookmark-line"></i>
          자주 쓰는 문구
          {templates.length > 0 && (
            <span className="rounded-full bg-secondary-200 px-1.5 text-[10px] font-bold text-secondary-900">
              {templates.length}
            </span>
          )}
          <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
        </button>
      </div>

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        rows={rows}
        placeholder={placeholder}
        className={`${inputClass} resize-none`}
      />

      {open && (
        <div className="rounded-lg border border-background-200 bg-background-100 p-3">
          {templates.length === 0 ? (
            <p className="text-xs text-foreground-500">
              아직 저장한 문구가 없어요. 아래에서 지금 내용을 저장해두면 다음부터 클릭 한 번에
              불러올 수 있어요.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {templates.map((t) => (
                <span
                  key={t.id}
                  className="flex max-w-full items-center gap-1 rounded-full border border-background-300 bg-background-50 py-1 pl-2.5 pr-1"
                >
                  <button
                    type="button"
                    onClick={() => insert(t.content)}
                    title={t.content}
                    className="max-w-[240px] truncate text-xs text-foreground-700 hover:text-primary-700"
                  >
                    {t.content}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteTemplate(t.id)}
                    title="문구 삭제"
                    className="flex h-5 w-5 items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-accent-100 hover:text-accent-700"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={!value.trim()}
              className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap ${
                savedFlash
                  ? 'border-primary-400 bg-primary-100 text-primary-700'
                  : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
            >
              <i className={savedFlash ? 'ri-check-line' : 'ri-add-line'}></i>
              {savedFlash ? '저장됨' : '현재 내용을 문구로 저장'}
            </button>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-lightbulb-line text-accent-600"></i>
              문구를 누르면 입력칸에 이어서 들어가요.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}